import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Link as LinkIcon, Image as ImageIcon, Download, ExternalLink } from 'lucide-react';
import { downloadFile } from '../utils/downloadFile';

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;

const DOC_TYPES = ['pdf', 'excel', 'word', 'powerpoint'];
const DOC_COLORS = { pdf: '#ef4444', excel: '#10b981', word: '#3b82f6', powerpoint: '#f97316' };

/**
 * WhatsApp-style "Media, links and docs" page for the open chat — three
 * independent tabs, each with the app's standard empty state.
 */
const MediaLinksDocsModal = ({ messages, chatName, onClose, onOpenImage, onOpenDoc, c, t, isRTL, theme }) => {
  const [tab, setTab] = useState('media');

  const { mediaItems, linkItems, docItems } = useMemo(() => {
    const list = messages || [];
    const media = list.filter((m) => m.type === 'image' && m.mediaUrl);
    const docs = list.filter((m) => DOC_TYPES.includes(m.type) && m.mediaUrl);
    const links = [];
    for (const m of list) {
      if (!m.text) continue;
      const found = String(m.text).match(URL_REGEX);
      if (found) {
        for (const url of found) links.push({ id: `${m.id}-${url}`, url, message: m });
      }
    }
    return { mediaItems: media.reverse(), linkItems: links.reverse(), docItems: docs.reverse() };
  }, [messages]);

  const tabs = [
    { id: 'docs', label: t.docsTab, count: docItems.length },
    { id: 'links', label: t.linksTab, count: linkItems.length },
    { id: 'media', label: t.mediaTab, count: mediaItems.length },
  ];

  const EmptyState = ({ icon, label }) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: c.textSecondary, padding: '40px 20px', textAlign: 'center' }}>
      {icon}
      <p style={{ fontSize: '15px', fontWeight: '600', marginTop: '14px', color: c.text }}>{label}</p>
    </div>
  );

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backdropFilter: 'blur(4px)',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560,
          background: theme === 'dark' ? '#1e293b' : '#ffffff',
          borderRadius: '24px', boxShadow: c.shadow, padding: '24px',
          height: '80vh', border: `1px solid ${c.border}`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: c.text }}>
            {t.mediaLinksDocs}
            <span style={{ fontSize: '13px', fontWeight: '500', color: c.textSecondary, marginInlineStart: '8px' }}>{chatName}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex' }}
            aria-label={t.cancel}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs — same pill style as the sidebar filter tabs */}
        <div style={{ display: 'flex', background: theme === 'dark' ? c.inputBg : '#e2e8f0', borderRadius: '14px', padding: '4px', marginBottom: '16px' }}>
          {tabs.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              style={{
                flex: 1, padding: '8px', borderRadius: '10px', border: 'none',
                background: tab === item.id ? (theme === 'dark' ? c.sidebarBg : '#ffffff') : 'transparent',
                color: tab === item.id ? c.text : c.textSecondary,
                fontWeight: tab === item.id ? '600' : '500',
                cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px',
                boxShadow: tab === item.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              {item.label}{item.count > 0 ? ` (${item.count})` : ''}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {tab === 'media' && (
            mediaItems.length === 0 ? (
              <EmptyState icon={<ImageIcon size={48} style={{ opacity: 0.4 }} />} label={t.noMediaFound} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px' }}>
                {mediaItems.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => onOpenImage(m)}
                    style={{
                      aspectRatio: '1', borderRadius: '12px', overflow: 'hidden',
                      cursor: 'pointer', border: `1px solid ${c.border}`,
                      background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                    }}
                  >
                    <img
                      src={m.mediaUrl || m.thumbnailUrl}
                      alt={m.mediaName || 'media'}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'links' && (
            linkItems.length === 0 ? (
              <EmptyState icon={<LinkIcon size={48} style={{ opacity: 0.4 }} />} label={t.noLinksFound} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {linkItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="employee-item-card"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 14px', borderRadius: '14px',
                      textDecoration: 'none', color: c.text,
                    }}
                  >
                    <div style={{ width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(46,124,186,0.12)', color: '#2e7cba' }}>
                      <LinkIcon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div dir="ltr" style={{ fontSize: '13.5px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: isRTL ? 'right' : 'left' }}>
                        {item.url}
                      </div>
                      <div style={{ fontSize: '11.5px', color: c.textSecondary, marginTop: '2px', direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {item.message.time}
                      </div>
                    </div>
                    <ExternalLink size={15} color={c.textSecondary} style={{ flexShrink: 0 }} />
                  </a>
                ))}
              </div>
            )
          )}

          {tab === 'docs' && (
            docItems.length === 0 ? (
              <EmptyState icon={<FileText size={48} style={{ opacity: 0.4 }} />} label={t.noDocsFound} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {docItems.map((m) => (
                  <div
                    key={m.id}
                    className="employee-item-card"
                    onClick={() => onOpenDoc(m)}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '14px', cursor: 'pointer' }}
                  >
                    <div style={{ width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${DOC_COLORS[m.type]}15`, color: DOC_COLORS[m.type] }}>
                      <FileText size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: '600', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.mediaName || `Document.${m.type}`}
                      </div>
                      <div style={{ fontSize: '11.5px', color: c.textSecondary, marginTop: '2px' }}>
                        {m.mediaSize ? `${(m.mediaSize / 1024).toFixed(0)} KB` : m.type.toUpperCase()} • <span style={{ direction: 'ltr' }}>{m.time}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      title={t.download}
                      onClick={(e) => { e.stopPropagation(); downloadFile(m.mediaUrl, m.mediaName); }}
                      style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex', padding: '6px', flexShrink: 0 }}
                    >
                      <Download size={17} />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MediaLinksDocsModal;
